import { defineStore } from "pinia"
import { z } from "zod"
import type { DropdownItem } from "#ui/types"
import type { ExcelI18n } from "~/utils/excel"
import type {
  DropdownItemScope,
  EventSchemaWithSilence,
  MeasureType,
  TSAcquisitionGroup,
} from "#imports"
import {
  BaseContractSchema,
  DocGenKind,
  DocumentSchemaShort,
  InvoiceSchemaShort,
  NoteSchema,
  ReportTargetSchema,
  Scope,
  SiteSchemaOverview,
  UIFeature,
} from "#imports"

export const ActionKind = z.enum([
  "documents",
  "upload_documents",
  //
  "docgen",
  //
  "gs_refresh",
  "gs_finance_refresh",
  //
  "dh_refresh",
  //
  "portfolio",
  "event_actions", // create / delete
  "excel_export",
  //
  "create_invoices",
  "invoice_actions", // compute / publish / submit
  "invoice_files", // invoice's PDF / Excel
  "invoice_set_submitted",
  "note_actions", // create / archive
  //
  "commissioning",
  "commissioning_extracts",
])
export type ActionKind = z.infer<typeof ActionKind>

interface ActionStoreInterface {
  // Documents
  selectedDocuments: DocumentSchemaShort[]
  showDeleteDocumentModal: boolean
  // Callback for bulk download - registered by DocumentsViewer.vue
  // (getters cannot call Vue Query hooks, so we use a callback pattern)
  downloadDocumentsHandler: (() => void) | null

  showDocumentUploadModal: boolean

  // DocGen
  showDocGenModal: boolean
  selectedDocgenTargets: ReportTargetSchema[]
  selectedDocgenKind: DocGenKind | undefined

  // GoldenSource
  showGSRefreshModal: boolean
  selectedGSRefreshTargets: ReportTargetSchema[]

  // GoldenSource Finance
  showGSFinanceRefreshModal: boolean

  // dataheath
  datahealthItems: DropdownItemScope[]

  // portfolio
  showSiteOrderDataModal: boolean
  showSiteIntradayModal: boolean
  selectedTsAcquisitionGroupforBackfill: TSAcquisitionGroup | MeasureType | undefined
  selectedSProviderForBackfill: string | undefined
  showSiteEditTimeseriesModal: boolean
  siteHasEditableTimeseries: boolean
  siteHasEnedisTimeseries: boolean

  // events
  selectedEvents: EventSchemaWithSilence[]
  showDeleteEventModal: boolean

  // event_actions
  eventActionsDropdownItems: DropdownItemScope[]

  // excel_export
  i18n: ExcelI18n | undefined
  excelExportModel: "Contract" | "Invoice" | "Site" | "Event" | "Note"
  excelExportItems:
    | BaseContractSchema[]
    | InvoiceSchemaShort[]
    | SiteSchemaOverview[]
    | EventSchemaWithSilence[]
    | NoteSchema[]

  // create_invoices
  selectedInvoiceTargets: InvoiceComputeTarget[]
  showInvoiceCreateModal: boolean

  // invoice_actions
  invoiceActionsDropdownItems: DropdownItemScope[]

  // invoice_files
  invoiceFilesDropdownItems: DropdownItemScope[]

  // invoice_set_submitted
  invoiceSetSubmittedDropdownItems: DropdownItemScope[]

  // notes
  noteActionsDropdownItems: DropdownItemScope[]

  // commissioning process
  showReserveCreateModal: boolean
  canCreateReserve: boolean
  isWorkAcceptanceOnlySite: boolean
}

export const useActionStore = defineStore("action", {
  state: () =>
    ({
      // Documents
      selectedDocuments: [],
      showDeleteDocumentModal: false,
      downloadDocumentsHandler: null,
      showDocumentUploadModal: false,

      // DocGen
      showDocGenModal: false,
      selectedDocgenTargets: [],
      selectedDocgenKind: undefined,

      // GoldenSource
      showGSRefreshModal: false,
      selectedGSRefreshTargets: [],

      // GoldenSource Finance
      showGSFinanceRefreshModal: false,

      // Datahealth
      datahealthItems: [],

      // portfolio
      showSiteOrderDataModal: false,
      showSiteIntradayModal: false,
      selectedSProviderForBackfill: undefined,
      selectedTsAcquisitionGroupforBackfill: undefined,
      showSiteEditTimeseriesModal: false,
      siteHasEditableTimeseries: false,
      siteHasEnedisTimeseries: false,

      // events
      selectedEvents: [],
      showDeleteEventModal: false,

      // event_actions
      eventActionsDropdownItems: [],

      // excel_export
      i18n: undefined,
      excelExportModel: "Contract",
      excelExportItems: [],

      // create_invoices
      selectedInvoiceTargets: [],
      showInvoiceCreateModal: false,

      // invoice_actions
      invoiceActionsDropdownItems: [],

      // invoice_files
      invoiceFilesDropdownItems: [],

      // invoice_set_submitted
      invoiceSetSubmittedDropdownItems: [],

      // notes
      noteActionsDropdownItems: [],

      // commissioning
      showReserveCreateModal: false,
      canCreateReserve: false,
      isWorkAcceptanceOnlySite: false,
    }) as ActionStoreInterface,
  getters: {
    actionDefs(state): Record<ActionKind, DropdownItem[]> {
      const docWithFile = state.selectedDocuments.filter((e) => e.file_exists)
      const mapItemsScopes: Record<ActionKind, (DropdownItemScope & { hidden?: boolean })[]> = {
        [ActionKind.enum.upload_documents]: [
          {
            label: "upload_contract",
            click: () => (state.showDocumentUploadModal = true),
            icon: "i-fa6-solid-file-pdf",
            scope: Scope.enum["upload:document"],
          },
        ],
        [ActionKind.enum.documents]: [
          {
            label: "download_files",
            click: () => state.downloadDocumentsHandler?.(),
            disabled: docWithFile.length === 0 || !state.downloadDocumentsHandler,
            icon: "i-fa6-solid-download",
            scope: Scope.enum["read:document"],
          },
          {
            label: "delete_files",
            click: () => (state.showDeleteDocumentModal = true),
            disabled: state.selectedDocuments.length === 0,
            icon: "i-fa6-solid-trash",
            scope: Scope.enum["write:document"],
          },
        ],
        [ActionKind.enum.docgen]: [
          {
            label: "generate_report",
            icon: "i-fa6-solid-file-circle-plus",
            click: () => (state.showDocGenModal = true),
            disabled: state.selectedDocgenTargets.length === 0,
            scope: Scope.enum["write:document"],
            hidden: state.isWorkAcceptanceOnlySite,
          },
        ],
        [ActionKind.enum.gs_refresh]: [
          {
            label: "gs_refresh",
            icon: "i-material-symbols-sync-rounded",
            click: () => (state.showGSRefreshModal = true),
            disabled: state.selectedGSRefreshTargets.length === 0,
            scope: Scope.enum["write:goldensource"],
          },
        ],
        [ActionKind.enum.gs_finance_refresh]: [
          {
            label: "gs_finance_refresh",
            icon: "i-material-symbols-sync-rounded",
            click: () => (state.showGSFinanceRefreshModal = true),
            scope: Scope.enum["write:goldensource"],
            hidden: state.isWorkAcceptanceOnlySite,
          },
        ],
        [ActionKind.enum.dh_refresh]: state.datahealthItems,
        [ActionKind.enum.portfolio]: [
          {
            label: "backfill",
            click: () => {
              state.showSiteOrderDataModal = true
              state.selectedSProviderForBackfill = undefined
              state.selectedTsAcquisitionGroupforBackfill = undefined
            },
            icon: "i-material-symbols-data-table-outline",
            scope: Scope.enum["order:timeseries"],
            hidden: state.isWorkAcceptanceOnlySite,
          },
          {
            label: "intraday",
            click: () => {
              state.showSiteIntradayModal = true
            },
            icon: "i-fa6-regular-calendar",
            scope: Scope.enum["order:timeseries"],
            disabled: !state.siteHasEnedisTimeseries,
            hidden: state.isWorkAcceptanceOnlySite,
          },
          {
            label: "ts_input_data",
            click: () => {
              state.showSiteEditTimeseriesModal = true
            },
            icon: $icons.chart_line,
            scope: Scope.enum["write:timeseries"],
            disabled: !state.siteHasEditableTimeseries,
            hidden: state.isWorkAcceptanceOnlySite,
          },
        ],
        [ActionKind.enum.event_actions]: state.eventActionsDropdownItems,
        [ActionKind.enum.excel_export]: [
          {
            label: "export_data",
            click: () => {
              if (!state.i18n) return
              dataToExcel(
                state.excelExportModel,
                "xlsx",
                formatDataToExcel(state.excelExportModel, state.excelExportItems, state.i18n),
              )
            },
            disabled: state.excelExportItems.length === 0,
            icon: "i-fa6-solid-file-export",
            scope: undefined,
          },
        ],
        [ActionKind.enum.create_invoices]: [
          {
            label: "create_invoices",
            click: () => (state.showInvoiceCreateModal = true),
            disabled: state.selectedInvoiceTargets.length === 0,
            icon: "i-fa6-regular-file-lines",
            scope: Scope.enum["write:invoice"],
          },
        ],
        [ActionKind.enum.invoice_actions]: state.invoiceActionsDropdownItems,
        [ActionKind.enum.invoice_files]: state.invoiceFilesDropdownItems,
        [ActionKind.enum.invoice_set_submitted]: state.invoiceSetSubmittedDropdownItems,
        [ActionKind.enum.note_actions]: state.noteActionsDropdownItems,
        [ActionKind.enum.commissioning]: [
          {
            label: "create_reserve",
            click: () => {
              state.showReserveCreateModal = true
            },
            icon: "i-heroicons-exclamation-triangle",
            scope: Scope.enum["write:supervision"],
            disabled: !state.canCreateReserve,
            // hide unless the user has the commissioning feature and creating a reserve is allowed
            hidden: !hasUIFeature(UIFeature.enum.commissioning_mgmt) || !state.canCreateReserve,
          },
        ],
        [ActionKind.enum.commissioning_extracts]: [
          {
            label: "extract_performance_tests",
            icon: "i-heroicons-arrow-down-tray",
            click: () => {
              state.selectedDocgenKind = DocGenKind.enum.performance_tests_extract
              state.showDocGenModal = true
            },
            disabled: state.selectedDocgenTargets.length === 0,
            scope: Scope.enum["write:document"],
          },
          {
            label: "extract_commissioning_reserves",
            icon: "i-heroicons-arrow-down-tray",
            click: () => {
              state.selectedDocgenKind = DocGenKind.enum.commissioning_reserves_extract
              state.showDocGenModal = true
            },
            disabled: state.selectedDocgenTargets.length === 0,
            scope: Scope.enum["write:document"],
          },
        ],
      }

      const mapItems = R.mapValues(mapItemsScopes, (values) =>
        values
          .filter(
            (item) =>
              (item.scope === undefined || hasScope(item.scope)) && !R.isTruthy(item.hidden),
          )
          .map((item) => R.pickBy(item, (_, key) => key !== "scope") as DropdownItem),
      )

      return mapItems
    },
  },
})
